import {
  dataResponseConverter,
  Endpoint,
  MakeRequest,
  RestResponse,
} from './request'
import type {
  FetchCurrencyModelResponse,
  GeoPositionItemModel,
  GeoPositionModel,
  LocationModel,
} from '../models'
import type {AppStorage} from '../index'

export class GeoApi {
  private cache = new Map<string, any>()

  constructor(private request: MakeRequest, private storage: AppStorage) {}

  fetchCityByRegion = async (
    id: string,
  ): Promise<RestResponse<Array<GeoPositionItemModel>>> => {
    const lang = this.storage.language
    const cacheKey = `cities_${lang}_${id}`
    if (this.cache.has(cacheKey)) return {result: this.cache.get(cacheKey)}

    const response = await this.request.request<Array<GeoPositionItemModel>>({
      api: Endpoint.cityList,
      body: {lang, region: id},
    })
    if (response.result && response.result.length > 0) {
      const converted = response.result.map((r) => ({
        id: r.id,
        code: r.code ?? null,
        word: r.word,
        value: r.word,
        key: r.id,
        has_adverts: r.has_adverts,
        slug: r.slug,
      }))
      this.cache.set(cacheKey, converted)
      return {result: converted}
    }

    return response
  }

  fetchRegionByCountry = async (
    id: string,
  ): Promise<RestResponse<Array<GeoPositionItemModel>>> => {
    const lang = this.storage.language
    const cacheKey = `regions_${lang}_${id}`
    if (this.cache.has(cacheKey)) return {result: this.cache.get(cacheKey)}

    const response = await this.request.request<Array<GeoPositionItemModel>>({
      api: Endpoint.regionList,
      body: {lang, country: id},
    })
    if (response.result && response.result.length > 0) {
      const converted = response.result.map((r) => ({
        id: r.id,
        code: r.code ?? null,
        word: r.word,
        value: r.word,
        key: r.id,
        slug: r.slug,
      }))
      this.cache.set(cacheKey, converted)
      return {result: converted}
    }
    return response
  }

  fetchPositionByGPS = (
    location: LocationModel,
  ): Promise<RestResponse<GeoPositionModel>> => {
    const lang = this.storage.language

    return this.request.request<GeoPositionModel>({
      api: Endpoint.locationByIp,
      body: {lat: location.latitude, lng: location.longitude, lang},
    })
  }

  fetchGPSByAddress = async (
    address: string,
  ): Promise<RestResponse<LocationModel>> => {
    const response = await this.request.request<any>({
      api: Endpoint.gpsLocation,
      body: {address},
    })
    if (response.error) return {error: response.error}
    return {
      result: {longitude: response.result!.lng, latitude: response.result!.lat},
    }
  }

  fetchGPSByIP = async (): Promise<RestResponse<LocationModel>> => {
    return await this.request.newRequest<LocationModel>(
      {
        endpoint: 'v2/geo/mylocation',
        method: 'GET',
      },
      dataResponseConverter,
    )
  }

  fetchCurrencyByLocation = async (location: LocationModel) => {
    const response = await this.request.request<FetchCurrencyModelResponse>({
      api: 'v1/get_currency_by_GPS.php',
      body: {
        lat: location.latitude,
        lng: location.longitude,
        lang: this.storage.language,
      },
    })
    return {result: response.result?.currency, error: response.error}
  }
}
